import { TooltipButton, Icon, FormMessage } from '~/components';
import React from 'react';
import { colors, weights } from '~/styles';
import _ from 'lodash';
import styled from 'styled-components';

const CircleButton = styled(TooltipButton)`
  width: 2.5rem;
  min-width: 2.5rem;
  max-width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background: ${colors.grey5};
  transition: all 100ms ease-in-out;
  border: none;
  color: ${colors.primary};
  flex: 1;
  &:hover {
    background-color: ${colors.grey10};
    color: ${colors.primary};
  }
`;

const BaseIndicator = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
  background-color: ${colors.white};
  color: ${colors.grey100};
  border: 1px solid ${colors.grey40};
`;

const Separator = styled.hr`
  margin: 0.75rem 0;
  padding: 0rem 0rem 0rem 0rem;
`;

const SkippedResults = styled.div`
  padding: 0rem 0rem 0rem 0rem;

  p {
    padding-left: 1.25rem;
    font-size: 0.75rem;
    font-style: italic;
  }
`;

const SkippedResultsBody = styled.div`
  padding-top: 1rem;
  display: flex;
  justify-content: space-between;
`;

const SkipCodesList = styled.ul`
  margin: 0;
  padding: 0rem 0 0 1.5rem;
  font-size: 0.875rem;

  li {
    margin: 0.5rem 0;
  }
`;

const SkipCodeHeader = styled.div`
  font-weight: ${weights.black};
  margin-top: 0.5rem;
`;

const SkipRowsList = styled.ul`
  color: ${colors.black};
  margin: 0.5rem 0.75rem;
  li {
    margin-top: 0rem;
  }
`;

const SkipDescription = styled.code`
  font-style: italic;
  font-size: 0.75rem;
  padding: 0 0.25rem;
`;

export const StyledErrorMessage = styled(FormMessage.Error)`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid ${colors.error};
  border-radius: 4px;
  background-color: ${colors.white};
  color: ${colors.grey100};

  ul {
    margin-top: 0.5rem;
    padding-left: 1.5rem; /* Indent for bullet points */
    list-style-type: disc; /* Bullet point style */
  }

  li {
    margin-bottom: 0.25rem; /* Space between bullet points */
  }
`;

const skipDescriptions = {
  idRequired: '"ID" is required, the value provided will be use for duplicate checks.',
  nameRequired: '"Name" is required.',
  emailRequired: '"Email" is required and will be use for duplicate checks.',
  duplicateMemberEmail: 'A Member with the same "Email" already exist and was not duplicated.',
  duplicateMemberInternalId:
    'A Member with the same Internal ID as the "ID" provided already exist and was not duplicated.',
  previouslyImportedLegacyId: '"Import ID" was used to check for duplicates, and found previously imported items.',
  previouslyImported: '"Import ID" was used to check for duplicates, and found previously imported items.',
  invalidDate: '"Date" was invalid and the row was skipped. Please provide dates in an ISO format (YYYY-MM-DD)',
  memberEmailNotFound: 'Could not find members with the following email addresses.',
  duplicateClient: 'Client(s) with the same name already exist and were not duplicated.',
  missingName: '"First Name" and "Last Name" are required.',
  invalidLedgerType: 'The Ledger provided was not valid.',
  invalidAmountRateQuantity: 'The amount did not match the calculated value from quantity and rate.',
  expenseCategoryNotFound: 'The expense category was not found.',
  missingImportId: 'The "Import ID" is required to be used for duplicate checks.',
  notesRequired: '"Notes" is required for "other" and "milestone" ledger items.',
  roleNameNotFound: '"Role Name" was not found on the project.',
  roleNameMissing: '"Role Name" was not provided for a project which uses roles.',
  duplicateRoleName: '"Role Name" provided already exists on the project.',
  duplicateTaskName: '"Task Name" provided already exists on the project.',
  categoryNotFound: '"Category" provided was not found, this must match an existing Expense Category.',
  clientNotFound: '"Client" was not found.',
  clientNameNotFound: '"Client Name" was not found for the following.',
  projectNotFound: '"Project" was not found for the following.',
  projectNameNotFound: '"Project Name" was not found for the following.',
  taskNameNotFound: '"Task Name" was not found on the project.',
  taskNameRequired: '"Task Name" was not provided, and is required.',
  taskNameMissing: '"Task Name" was not provided for a project which requires tasks on time entry and allocations.',
  invalidAmount: '"Amount" is not valid, and could not be read in as a numeric value.',
  invalidCurrency: '"Currency" provided was not valid, please provide a valid currency code (i.e. "USD", "GBP", etc)',
  invoiceLineAlreadyExists:
    'Lines on an invoice each have a line number, and can not be re-used. Please check the intended line number for the following rows.',
  projectCurrencyMismatch:
    "The project's currency does not match the invoice currency. Please ensure the project and invoice use the same currency.",
};

export function ImportResults({ result }) {
  const { skipped } = result;
  const handleCopyToClipboard = () => {
    const textToCopy = _.map(skipped.reasons, (reason, key) => {
      const skipDescription = skipDescriptions[key];
      const rowsText = _.map(reason, (skippedRows, skipItemValue) => {
        const itemText =
          skipItemValue && skipItemValue != 'undefined'
            ? `"${skipItemValue}" on rows: ${skippedRows.rowNumber.join(', ')}`
            : `Review rows: ${skippedRows.rowNumber.join(', ')}`;

        return itemText;
      }).join('\n');
      return `Skip Code: ${key}\n${skipDescription ? `${skipDescription}\n` : ''}${rowsText}`;
    }).join('\n\n');

    navigator.clipboard.writeText(textToCopy).then(
      () => {
        // eslint-disable-next-line no-console
        console.log('Copied to clipboard');
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Could not copy text: ', err);
      },
    );
  };

  return (
    <BaseIndicator>
      <div>
        <Icon icon="check" color={colors.success}></Icon>&nbsp;&nbsp;
        <strong>{result.recordsCreated} record(s) were successfully imported.</strong>
      </div>
      <Separator />

      {skipped?.totalCount > 0 && (
        <SkippedResults>
          <Icon icon="exclamation-circle" color={colors.warning100}></Icon>&nbsp;&nbsp;
          <strong>{skipped.totalCount} record(s) were skipped during import.</strong>
          <p>Skipped records can be revised and re-imported, please review the details below.</p>
          <SkippedResultsBody>
            <SkipCodesList>
              {_.map(skipped.reasons, (reason, key) => {
                return (
                  <li key={key}>
                    <SkipCodeHeader>
                      {skipDescriptions[key] ?? <SkipDescription>Skip Code: {key}</SkipDescription>}
                    </SkipCodeHeader>
                    <SkipRowsList>
                      {_.map(reason, (skippedRows, skipItemValue) => {
                        return skipItemValue ? (
                          <li key={skipItemValue}>
                            {skipItemValue && skipItemValue != 'undefined'
                              ? `'${skipItemValue}' on rows: `
                              : 'Review rows: '}
                            {skippedRows.rowNumber.join(', ')}
                          </li>
                        ) : (
                          <li>Review rows: {skippedRows.rowNumber.join(', ')}</li>
                        );
                      })}
                    </SkipRowsList>
                  </li>
                );
              })}
            </SkipCodesList>

            <CircleButton onClick={handleCopyToClipboard} tooltip={'Copy skip information to clipboard'}>
              <Icon icon="clipboard" />
            </CircleButton>
          </SkippedResultsBody>
        </SkippedResults>
      )}
    </BaseIndicator>
  );
}
